import { useState, useEffect } from "react"
import { PlusCircle, X, Plus, Trash2, Sparkles, Loader2, Brain, BookOpen, Info, Clock, ToggleLeft, ToggleRight, CheckCircle2, AlertCircle } from "lucide-react"
import { aiService } from "@/hooks/api/aiService"
import { SchoolLayout } from "../components/ui/school/SchoolLayout"
import {
  listPsychometricAssessments, togglePsychometricPermission,
  listAssessments, createAssessment, updateAssessment, deleteAssessment,
} from "../hooks/api/schoolApi"

// ─── Helpers ──────────────────────────────────────────────────────────────────

const newOption = (id) => ({ id, text: "", traitId: "", traitValue: 5 })
const newQuestion = () => ({
  id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
  text: "",
  type: "multiple_choice",
  options: ["A", "B", "C", "D"].map(newOption),
  correctAnswer: "",
  points: 1,
})

// ─── Explainer Banner ─────────────────────────────────────────────────────────

function ExplainerBanner() {
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem("assess-banner-v2-dismissed"))
  if (dismissed) return null
  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5 flex gap-4 mb-6">
      <Info className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
      <div className="flex-1 space-y-2">
        <p className="font-bold text-indigo-900 text-sm">Two different things — Assessments vs Quizzes</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="bg-white rounded-xl border border-indigo-100 p-3">
            <div className="flex items-center gap-2 mb-1">
              <Brain className="w-4 h-4 text-violet-600" />
              <span className="font-bold text-slate-800">Psychometric Assessments</span>
            </div>
            <p className="text-slate-500 text-xs leading-relaxed">
              Created globally by EarlyScape. No right or wrong answers — responses build the student's
              career trait profile. You can enable or disable each one for your school's students.
            </p>
          </div>
          <div className="bg-white rounded-xl border border-indigo-100 p-3">
            <div className="flex items-center gap-2 mb-1">
              <BookOpen className="w-4 h-4 text-teal-600" />
              <span className="font-bold text-slate-800">Subject Quizzes</span>
            </div>
            <p className="text-slate-500 text-xs leading-relaxed">
              Created by you and your staff. These have correct answers, are graded, and help students
              practice subject knowledge. Students earn XP for right answers.
            </p>
          </div>
        </div>
      </div>
      <button
        onClick={() => { setDismissed(true); localStorage.setItem("assess-banner-v2-dismissed", "1") }}
        className="p-1 hover:bg-indigo-100 rounded-lg self-start"
      >
        <X className="w-4 h-4 text-indigo-400" />
      </button>
    </div>
  )
}

// ─── Psychometric Card (global catalogue with toggle) ─────────────────────────

function PsychometricCatalogueCard({ assessment, onToggle, toggling }) {
  const id = assessment._id ?? assessment.id
  return (
    <div className={`bg-white border rounded-2xl p-5 transition-all ${assessment.permitted ? "border-violet-200 bg-violet-50/30" : "border-slate-200"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-violet-50 border border-violet-100 flex items-center justify-center shrink-0">
            <Brain className="w-4 h-4 text-violet-600" />
          </div>
          <div>
            <p className="font-bold text-slate-800">{assessment.title}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {assessment.questions?.length ?? 0} questions
              {assessment.subject && assessment.subject !== "General" ? ` · ${assessment.subject}` : ""}
            </p>
          </div>
        </div>
        <button
          onClick={() => onToggle(id)}
          disabled={toggling === id}
          className="shrink-0 disabled:opacity-50 transition-colors"
          title={assessment.permitted ? "Disable for your students" : "Enable for your students"}
        >
          {toggling === id
            ? <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
            : assessment.permitted
              ? <ToggleRight className="w-7 h-7 text-violet-600" />
              : <ToggleLeft className="w-7 h-7 text-slate-300" />
          }
        </button>
      </div>

      {assessment.description && (
        <p className="text-sm text-slate-500 mt-3 leading-relaxed">{assessment.description}</p>
      )}

      <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
        {assessment.timeLimit && (
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {assessment.timeLimit} min
          </span>
        )}
        <span className={`ml-auto font-semibold px-2 py-0.5 rounded-full border text-xs ${
          assessment.permitted
            ? "bg-violet-50 border-violet-200 text-violet-700"
            : "bg-slate-50 border-slate-200 text-slate-400"
        }`}>
          {assessment.permitted ? "Enabled for your students" : "Disabled"}
        </span>
      </div>
    </div>
  )
}

// ─── Create / Edit Quiz Modal ─────────────────────────────────────────────────

function QuizModal({ existing, onClose, onSave }) {
  const isEdit = !!existing
  const [title, setTitle] = useState(existing?.title ?? "")
  const [subject, setSubject] = useState(existing?.subject ?? "")
  const [classLevel, setClassLevel] = useState(existing?.classLevel ?? "")
  const [passingScore, setPassingScore] = useState(existing?.passingScore ?? 50)
  const [timeLimit, setTimeLimit] = useState(existing?.timeLimit ?? 30)
  const [questions, setQuestions] = useState(
    existing?.questions?.length ? existing.questions : [newQuestion()]
  )
  const [isGenerating, setIsGenerating] = useState(false)
  const [saving, setSaving] = useState(false)

  const addQuestion = () => setQuestions(prev => [...prev, newQuestion()])
  const removeQuestion = (idx) => setQuestions(prev => prev.filter((_, i) => i !== idx))
  const updateQ = (idx, field, value) =>
    setQuestions(prev => prev.map((q, i) => i === idx ? { ...q, [field]: value } : q))
  const updateOption = (qIdx, optId, field, value) =>
    setQuestions(prev => prev.map((q, i) =>
      i === qIdx ? { ...q, options: q.options.map(o => o.id === optId ? { ...o, [field]: value } : o) } : q
    ))

  const handleGenerateWithAI = async () => {
    if (!subject.trim() && !title.trim()) return
    setIsGenerating(true)
    try {
      const topic = subject.trim() || title.trim()
      const prompt = `Generate 5 multiple-choice quiz questions on "${topic}" for ${classLevel || "secondary school"} students. Each question must have one correct answer (A, B, C, or D) and 3 plausible distractors. Return ONLY a JSON array: [{"text": "question text", "options": [{"id": "A", "text": "option text"}, ...], "correctAnswer": "A", "explanation": "why A is correct"}]`
      const response = await aiService.ask(prompt, "You are a curriculum expert creating graded quiz questions.")
      const jsonMatch = response.match(/\[[\s\S]*\]/)
      if (!jsonMatch) throw new Error("No JSON")
      const parsed = JSON.parse(jsonMatch[0])
      setQuestions(parsed.slice(0, 5).map(q => ({
        id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        text: q.text ?? "",
        type: "multiple_choice",
        options: (q.options ?? ["A","B","C","D"].map(id => ({ id, text: "" }))).map(o => ({
          id: o.id, text: o.text ?? "",
        })),
        correctAnswer: q.correctAnswer ?? "A",
        explanation: q.explanation ?? "",
        points: 1,
      })))
    } catch (err) {
      console.warn("AI generation failed:", err)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleSave = async () => {
    if (!title.trim() || !subject.trim() || !classLevel.trim()) return
    setSaving(true)
    try {
      const payload = {
        title: title.trim(),
        subject: subject.trim(),
        classLevel: classLevel.trim(),
        category: "topic_quiz",
        passingScore: Number(passingScore),
        timeLimit: Number(timeLimit),
        questions: questions.map(q => ({
          id: q.id,
          text: q.text,
          type: "multiple_choice",
          options: q.options.map(o => ({ id: o.id, text: o.text })),
          correctAnswer: q.correctAnswer,
          explanation: q.explanation ?? "",
          points: Number(q.points ?? 1),
        })),
        status: "published",
      }
      if (isEdit) {
        await updateAssessment(existing._id ?? existing.id, payload)
      } else {
        await createAssessment(payload)
      }
      onSave()
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-teal-600" />
            <div>
              <h2 className="text-xl font-bold text-gray-900">{isEdit ? "Edit Quiz" : "Create Quiz"}</h2>
              <p className="text-xs text-slate-400">Has correct answers · students earn XP for right responses</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-3">
              <label className="block text-sm font-semibold text-gray-700 mb-1">Title *</label>
              <input value={title} onChange={e => setTitle(e.target.value)}
                placeholder="e.g. SS2 Biology — Cell Division"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Subject *</label>
              <input value={subject} onChange={e => setSubject(e.target.value)}
                placeholder="e.g. Biology"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Class level *</label>
              <input value={classLevel} onChange={e => setClassLevel(e.target.value)}
                placeholder="e.g. SS2"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Time limit (min)</label>
              <input type="number" min={5} value={timeLimit} onChange={e => setTimeLimit(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500" />
            </div>
          </div>

          {/* Questions */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900">Questions</h3>
              <div className="flex items-center gap-2">
                <button onClick={handleGenerateWithAI}
                  disabled={isGenerating || (!subject.trim() && !title.trim())}
                  className="flex items-center gap-1.5 text-sm bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-semibold px-3 py-1.5 rounded-lg transition-all">
                  {isGenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Generate with AI
                </button>
                <button onClick={addQuestion} className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700 font-semibold">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
            </div>

            <div className="space-y-4">
              {questions.map((q, qIdx) => (
                <div key={q.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="mt-2 text-xs font-bold text-gray-400 w-5 shrink-0">{qIdx + 1}.</span>
                    <input value={q.text} onChange={e => updateQ(qIdx, "text", e.target.value)}
                      placeholder="Question text"
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white" />
                    {questions.length > 1 && (
                      <button onClick={() => removeQuestion(qIdx)} className="mt-1 p-1.5 rounded-lg hover:bg-red-100 text-red-400 hover:text-red-600">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-2 ml-8">
                    <div className="grid grid-cols-12 gap-2 text-xs font-bold text-gray-400 uppercase tracking-wide px-1">
                      <span className="col-span-1">Opt</span>
                      <span className="col-span-9">Answer text</span>
                      <span className="col-span-2 text-center">Correct</span>
                    </div>
                    {q.options.map(opt => (
                      <div key={opt.id} className="grid grid-cols-12 gap-2 items-center">
                        <span className="col-span-1 text-xs font-bold text-gray-500 text-center">{opt.id}</span>
                        <input value={opt.text} onChange={e => updateOption(qIdx, opt.id, "text", e.target.value)}
                          placeholder={`Option ${opt.id}`}
                          className="col-span-9 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white" />
                        <div className="col-span-2 flex justify-center">
                          <input
                            type="radio"
                            name={`correct-${q.id}`}
                            checked={q.correctAnswer === opt.id}
                            onChange={() => updateQ(qIdx, "correctAnswer", opt.id)}
                            className="w-4 h-4 accent-teal-600"
                          />
                        </div>
                      </div>
                    ))}
                    {q.explanation !== undefined && (
                      <input value={q.explanation ?? ""} onChange={e => updateQ(qIdx, "explanation", e.target.value)}
                        placeholder="Explanation (optional) — shown to student after answering"
                        className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-teal-400 bg-white mt-1" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button onClick={onClose}
            className="px-5 py-2.5 text-sm font-semibold text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !title.trim() || !subject.trim() || !classLevel.trim()}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-50 rounded-lg">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? "Save Changes" : "Create Quiz"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Quiz Card ────────────────────────────────────────────────────────────────

function QuizCard({ quiz, onEdit, onDelete }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
            <BookOpen className="w-4 h-4 text-teal-600" />
          </div>
          <div>
            <p className="font-bold text-slate-800">{quiz.title}</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {quiz.subject} · {quiz.classLevel} · {quiz.questions?.length ?? 0} questions
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => onEdit(quiz)}
            className="p-1.5 hover:bg-teal-50 rounded-lg text-slate-400 hover:text-teal-600 transition-colors text-xs font-semibold px-2">
            Edit
          </button>
          <button onClick={() => onDelete(quiz._id ?? quiz.id)}
            className="p-1.5 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
        {quiz.timeLimit && (
          <span className="flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {quiz.timeLimit} min
          </span>
        )}
        <span className="flex items-center gap-1">
          <CheckCircle2 className="w-3.5 h-3.5 text-teal-500" />
          Pass: {quiz.passingScore ?? 50}%
        </span>
        <span className={`ml-auto font-semibold px-2 py-0.5 rounded-full border text-xs ${
          quiz.status === "published"
            ? "bg-teal-50 border-teal-200 text-teal-700"
            : "bg-slate-50 border-slate-200 text-slate-400"
        }`}>
          {quiz.status ?? "draft"}
        </span>
      </div>
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

const AssessmentManagementPage = () => {
  const [tab, setTab] = useState("psychometric")
  const [psychometrics, setPsychometrics] = useState([])
  const [quizzes, setQuizzes] = useState([])
  const [loadingPsych, setLoadingPsych] = useState(true)
  const [loadingQuiz, setLoadingQuiz] = useState(true)
  const [psychError, setPsychError] = useState(null)
  const [quizError, setQuizError] = useState(null)
  const [toggling, setToggling] = useState(null)
  const [showQuizModal, setShowQuizModal] = useState(false)
  const [editingQuiz, setEditingQuiz] = useState(null)

  const loadPsychometrics = () => {
    setLoadingPsych(true)
    setPsychError(null)
    listPsychometricAssessments()
      .then(setPsychometrics)
      .catch(err => setPsychError(err?.response?.data?.error?.message ?? "Could not load assessments."))
      .finally(() => setLoadingPsych(false))
  }

  const loadQuizzes = () => {
    setLoadingQuiz(true)
    setQuizError(null)
    listAssessments({ category: "topic_quiz" })
      .then(data => setQuizzes(Array.isArray(data) ? data : (data?.assessments ?? [])))
      .catch(err => setQuizError(err?.response?.data?.error?.message ?? "Could not load quizzes."))
      .finally(() => setLoadingQuiz(false))
  }

  useEffect(() => { loadPsychometrics(); loadQuizzes() }, [])

  const handleToggle = async (id) => {
    setToggling(id)
    try {
      const { permitted } = await togglePsychometricPermission(id)
      setPsychometrics(prev => prev.map(a =>
        (a._id ?? a.id) === id ? { ...a, permitted } : a
      ))
    } catch (err) {
      console.error(err)
    } finally {
      setToggling(null)
    }
  }

  const handleDeleteQuiz = async (id) => {
    await deleteAssessment(id).catch(() => {})
    setQuizzes(prev => prev.filter(q => (q._id ?? q.id) !== id))
  }

  const enabledCount = psychometrics.filter(a => a.permitted).length

  return (
    <SchoolLayout>
      <div className="p-4 md:p-8 overflow-y-auto">

        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Assessments & Quizzes</h1>
            <p className="text-gray-500 mt-1 text-sm">
              Enable psychometric assessments for your students, and create graded subject quizzes.
            </p>
          </div>
          {tab === "quizzes" && (
            <button
              onClick={() => { setEditingQuiz(null); setShowQuizModal(true) }}
              className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-bold px-5 py-2.5 rounded-xl transition-colors"
            >
              <PlusCircle className="w-4 h-4" />
              Create Quiz
            </button>
          )}
        </div>

        <ExplainerBanner />

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit mb-6">
          <button
            onClick={() => setTab("psychometric")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              tab === "psychometric" ? "bg-white shadow-sm text-violet-700" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <Brain className="w-4 h-4" />
            Psychometric
            {enabledCount > 0 && (
              <span className="text-xs font-bold bg-violet-100 text-violet-600 px-1.5 py-0.5 rounded-full">
                {enabledCount} on
              </span>
            )}
          </button>
          <button
            onClick={() => setTab("quizzes")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              tab === "quizzes" ? "bg-white shadow-sm text-teal-700" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Quizzes
            {quizzes.length > 0 && (
              <span className="text-xs font-bold bg-teal-100 text-teal-600 px-1.5 py-0.5 rounded-full">
                {quizzes.length}
              </span>
            )}
          </button>
        </div>

        {/* Psychometric tab — global catalogue with toggles */}
        {tab === "psychometric" && (
          <div>
            {loadingPsych ? (
              <div className="flex items-center gap-3 justify-center py-16 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading catalogue…</span>
              </div>
            ) : psychError ? (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl p-5 text-red-700 max-w-lg">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <p className="text-sm font-medium">{psychError}</p>
              </div>
            ) : psychometrics.length === 0 ? (
              <div className="text-center py-16 space-y-3">
                <Brain className="w-12 h-12 text-slate-300 mx-auto" />
                <p className="font-bold text-slate-700">No psychometric assessments available yet</p>
                <p className="text-slate-400 text-sm max-w-sm mx-auto">
                  EarlyScape will publish assessments here. Once published, you can enable them for your students.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-4">
                  Toggle on the assessments you want your students to take. Enabled assessments appear on each student's assessment list.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {psychometrics.map(a => (
                    <PsychometricCatalogueCard
                      key={a._id ?? a.id}
                      assessment={a}
                      onToggle={handleToggle}
                      toggling={toggling}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Quizzes tab — school admin CRUD */}
        {tab === "quizzes" && (
          <div>
            {loadingQuiz ? (
              <div className="flex items-center gap-3 justify-center py-16 text-slate-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading quizzes…</span>
              </div>
            ) : quizError ? (
              <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl p-5 text-red-700 max-w-lg">
                <AlertCircle className="w-5 h-5 shrink-0" />
                <p className="text-sm font-medium">{quizError}</p>
              </div>
            ) : quizzes.length === 0 ? (
              <div className="text-center py-16 space-y-3">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto" />
                <p className="font-bold text-slate-700">No quizzes yet</p>
                <p className="text-slate-400 text-sm max-w-sm mx-auto">
                  Create your first quiz to test students on subject knowledge. Quizzes are graded and students earn XP.
                </p>
                <button
                  onClick={() => { setEditingQuiz(null); setShowQuizModal(true) }}
                  className="inline-flex items-center gap-2 bg-teal-600 text-white font-bold px-5 py-2.5 rounded-xl hover:bg-teal-700 transition-colors"
                >
                  <PlusCircle className="w-4 h-4" />
                  Create Quiz
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {quizzes.map(q => (
                  <QuizCard
                    key={q._id ?? q.id}
                    quiz={q}
                    onEdit={(quiz) => { setEditingQuiz(quiz); setShowQuizModal(true) }}
                    onDelete={handleDeleteQuiz}
                  />
                ))}
              </div>
            )}
          </div>
        )}

      </div>

      {showQuizModal && (
        <QuizModal
          existing={editingQuiz}
          onClose={() => { setShowQuizModal(false); setEditingQuiz(null) }}
          onSave={() => { setShowQuizModal(false); setEditingQuiz(null); loadQuizzes() }}
        />
      )}
    </SchoolLayout>
  )
}

export default AssessmentManagementPage
